import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiArrowUp, FiArrowDown, FiUsers, FiDownload } from 'react-icons/fi';
import Tooltip from '@mui/material/Tooltip';
import { useProfiles } from '../../hooks/useProfiles';
import {
    PageHeader,
    StatusBadge,
    EmptyState,
    SkeletonTableRow,
    SearchInput,
    useToast,
} from '../../theme/components';
import { PageButton } from '../../theme/components/PageButton';
import Avatar from '../../theme/components/Avatar';
import { decimalAgeYears, formatDateDisplay } from '../../utils/dates';
import { APP_NAME } from '../../config';
import { describeFilters } from '../../utils/filters';
import { buildPdfReport } from '../../utils/pdfReport';
import {
    growthSeverity,
    GROWTH_STATUSES,
    GROWTH_STATUS_DEFAULT,
} from '../../utils/growthStatus';
import { useAuth } from '../../auth/AuthContext';
import './Profiles.css';

// "Added by" is admin-only (the value is the PHMxxxx business code, not
// the human name). Non-admins see the registration list without the
// attribution column — the existing ChildListItem schema docstring
// already states "visible only to admins".
const BASE_COLUMNS = [
    { key: 'childNo', label: 'Reg: No', sortable: true, width: '110px' },
    { key: 'name', label: 'Child Name', sortable: true },
    { key: 'category', label: 'Category', sortable: true, width: '150px' },
    { key: 'dob', label: 'DOB', sortable: true, width: '110px' },
    { key: 'mothersNo', label: "Mother's No", sortable: true, width: '110px' },
    { key: 'vaccination', label: 'Vaccination', sortable: true, width: '220px' },
    { key: 'growth', label: 'Growth', sortable: true, width: '110px' },
    { key: 'gnDevision', label: 'GN Devision', sortable: true, width: '130px' },
    { key: 'ownerIdCode', label: 'Added by', sortable: true, width: '150px' },
];

/**
 * Three-bucket age category that matches the Growth page's
 * Early Childhood / Preschool Age / School Age subtabs.
 */
const getCategory = (dobString) => {
    if (!dobString) return '—';
    const years = decimalAgeYears(dobString);
    if (years <= 2) return 'Early Childhood';
    if (years <= 5) return 'Preschool Age';
    return 'School Age';
};

const categorySeverity = (category) => {
    if (category === 'Early Childhood') return 'info';
    if (category === 'Preschool Age') return 'success';
    if (category === 'School Age') return 'warning';
    return 'neutral';
};

// Legacy `location.state.filter` is one string set by dashboard tiles.
// Map it to the right one of our four dropdowns.
const splitLegacyFilter = (value) => {
    const out = { age: 'All', vacc: 'All', growth: 'All', gn: 'All' };
    if (!value || value === 'All') return out;
    if (value === 'Infant' || value === 'EarlyChildhood') out.age = 'EarlyChildhood';
    else if (value === 'Child' || value === 'Preschool') out.age = 'Preschool';
    else if (value === 'SchoolAge') out.age = 'SchoolAge';
    else if (GROWTH_STATUSES.includes(value)) out.growth = value;
    else if (['Overdue', 'Upcoming', 'Due', 'Up to Date'].includes(value)) out.vacc = value;
    else out.gn = value;
    return out;
};

// UI value → backend ?age= param value.
const AGE_PARAM = {
    EarlyChildhood: 'early_childhood',
    Preschool: 'preschool',
    SchoolAge: 'school_age',
};

// Module-scope constants used by the result-card memo. Hoisted out of the
// component so they don't bust useMemo's exhaustive-deps check.
const AGE_LABEL = {
    EarlyChildhood: 'Early Childhood',
    Preschool: 'Preschool Age',
    SchoolAge: 'School Age',
};
const VACC_LABEL = {
    Overdue: 'Overdue',
    Due: 'Due',
    Upcoming: 'Upcoming',
    'Up to Date': 'Up to date',
};
const vaccSeverity = (label) => {
    if (label === 'Overdue') return 'danger';
    if (label === 'Due') return 'warning';
    if (label === 'Upcoming') return 'info';
    if (label === 'Up to date') return 'success';
    return 'neutral';
};

const Profiles = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();
    const { user } = useAuth();
    // "Added by" is admin-only. Non-admins don't see the column at all.
    const isAdmin = user?.role === 'admin';
    const COLUMNS = isAdmin
        ? BASE_COLUMNS
        : BASE_COLUMNS.filter((c) => c.key !== 'ownerIdCode');

    const initial = splitLegacyFilter(location.state?.filter);
    const [searchQuery, setSearchQuery] = useState('');
    const [ageFilter, setAgeFilter] = useState(initial.age);
    const [vaccFilter, setVaccFilter] = useState(initial.vacc);
    const [growthFilter, setGrowthFilter] = useState(initial.growth);
    const [gnFilter, setGnFilter] = useState(initial.gn);
    const [sort, setSort] = useState({ key: 'childNo', dir: 'asc' });

    useEffect(() => {
        const next = splitLegacyFilter(location.state?.filter);
        setAgeFilter(next.age);
        setVaccFilter(next.vacc);
        setGrowthFilter(next.growth);
        setGnFilter(next.gn);
    }, [location.key, location.state?.filter]);

    const apiFilters = useMemo(() => {
        const f = {};
        if (AGE_PARAM[ageFilter]) f.age = AGE_PARAM[ageFilter];
        if (vaccFilter !== 'All') f.vaccination = vaccFilter;
        if (growthFilter !== 'All') f.growth = growthFilter;
        if (gnFilter !== 'All') f.gn_devision = gnFilter;
        return f;
    }, [ageFilter, vaccFilter, growthFilter, gnFilter]);

    const { data: apiData, loading, error } = useProfiles(apiFilters);
    const childrenData = useMemo(() => apiData || [], [apiData]);

    // ponytail: GN list pulled off whatever's already loaded — good until the
    // catalogue grows past ~hundreds; then expose /profiles/gn-divisions.
    const uniqueGNDivisions = useMemo(
        () =>
            [...new Set(childrenData.map((c) => c.gnDevision).filter(Boolean))].sort(),
        [childrenData],
    );

    // Search stays client-side (typed-as-you-go); dropdowns filter server-side.
    const searchedData = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return childrenData;
        return childrenData.filter(
            (c) =>
                c.name?.toLowerCase().includes(q) ||
                c.childNo?.toLowerCase().includes(q) ||
                c.mothersNo?.toLowerCase().includes(q),
        );
    }, [childrenData, searchQuery]);

    // Cards reflect the active filter set. Total appears only when nothing
    // else is selected; otherwise one card per active filter. An active
    // search short-circuits to a single Total card with the search count
    // — dropdown cards don't re-render against a query the user can see
    // typed in the input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const activeCards = useMemo(() => {
        const count = searchedData.length;
        if (searchQuery.trim()) {
            return [{ key: 'total', label: 'Total', severity: 'primary', count }];
        }
        const cards = [];
        if (ageFilter !== 'All') {
            const label = AGE_LABEL[ageFilter] || ageFilter;
            cards.push({ key: 'age', label, severity: categorySeverity(label) });
        }
        if (vaccFilter !== 'All') {
            const label = VACC_LABEL[vaccFilter] || vaccFilter;
            cards.push({ key: 'vacc', label, severity: vaccSeverity(label) });
        }
        if (growthFilter !== 'All') {
            cards.push({
                key: 'growth',
                label: growthFilter,
                severity: growthSeverity(growthFilter),
            });
        }
        if (gnFilter !== 'All') {
            cards.push({ key: 'gn', label: gnFilter, severity: 'neutral' });
        }
        if (cards.length === 0) {
            cards.push({ key: 'total', label: 'Total', severity: 'primary' });
        }
        return cards.map((c) => ({ ...c, count }));
    }, [
        searchQuery,
        ageFilter,
        vaccFilter,
        growthFilter,
        gnFilter,
        searchedData.length,
        // Module-scope constants — referenced in the closure, listed for
        // exhaustive-deps. References are stable so no memo churn.
        AGE_LABEL,
        VACC_LABEL,
    ]);

    const sortedData = useMemo(() => {
        const dir = sort.dir === 'asc' ? 1 : -1;
        const getSortValue = (child, key) => {
            switch (key) {
                case 'name':
                    return child.name || '';
                case 'childNo':
                    return child.childNo || '';
                case 'dob':
                    return child.dob || '';
                case 'mothersNo':
                    return child.mothersNo || '';
                case 'gnDevision':
                    return child.gnDevision || '';
                case 'category':
                    return getCategory(child.dob);
                case 'vaccination':
                    return child.vaccinationStatus || 'Completed';
                case 'ownerIdCode':
                    return child.ownerIdCode || '—';
                case 'growth': {
                    const statuses =
                        Array.isArray(child.growthStatuses) && child.growthStatuses.length > 0
                            ? child.growthStatuses
                            : [GROWTH_STATUS_DEFAULT];
                    // Sort by most severe first (lower index in GROWTH_STATUSES
                    // = more urgent), so the column reads consistently when
                    // users click the header.
                    return statuses
                        .map((s) => ({ s, i: GROWTH_STATUSES.indexOf(s) }))
                        .sort((a, b) => a.i - b.i)
                        .map((x) => x.s)
                        .join(', ');
                }
                default:
                    return '';
            }
        };
        return [...searchedData].sort((a, b) => {
            const av = getSortValue(a, sort.key);
            const bv = getSortValue(b, sort.key);
            if (av < bv) return -1 * dir;
            if (av > bv) return 1 * dir;
            return 0;
        });
    }, [searchedData, sort]);

    const titleInfo = useMemo(() => {
        const active = [];
        if (ageFilter !== 'All') active.push(ageFilter);
        if (vaccFilter !== 'All') active.push(vaccFilter);
        if (growthFilter !== 'All') active.push(growthFilter);
        if (gnFilter !== 'All') active.push(gnFilter);

        if (active.length === 0) {
            return {
                title: 'Registered Profiles',
                subtitle: 'Overview of all children registered in the system.',
            };
        }
        if (active.length > 1) {
            return {
                title: 'Filtered Profiles',
                subtitle: `Showing children matching: ${active.join(', ')}.`,
            };
        }
        const sole = active[0];
        const map = {
            EarlyChildhood: ['Early Childhood Profiles', 'Children currently in the early-childhood age range (0 – 2 years).'],
            Preschool: ['Preschool Age Profiles', 'Children currently in the preschool age range (2 – 5 years).'],
            SchoolAge: ['School Age Profiles', 'Children currently in the school age range (5 – 11 years).'],
            Overdue: ['Overdue Vaccinations', 'Children with vaccination records past their scheduled due dates.'],
            Upcoming: ['Upcoming Vaccinations', 'Children with vaccinations scheduled in the next 14 days.'],
            Due: ['Due Vaccinations', 'Children with vaccination records currently due.'],
            'Up to Date': ['Up-to-Date Profiles', 'Children who have completed all scheduled vaccinations.'],
            Normal: ['Normal Growth Profiles', 'Children whose growth status is currently marked as normal.'],
            Underweight: ['Underweight Children', 'Children whose growth status is currently marked as underweight.'],
            'Severely Underweight': ['Severely Underweight Children', 'Children whose weight falls below -3SD — needs urgent follow-up.'],
            Overweight: ['Overweight Children', 'Children whose growth status is currently marked as overweight.'],
            Obese: ['Obese Children', 'Children whose weight exceeds +3SD — needs follow-up.'],
        };
        if (map[sole]) return { title: map[sole][0], subtitle: map[sole][1] };
        return {
            title: `GN Division: ${sole}`,
            subtitle: `Overview of children registered in GN Division ${sole}.`,
        };
    }, [ageFilter, vaccFilter, growthFilter, gnFilter]);

    const toggleSort = (key) => {
        setSort((prev) => {
            if (prev.key === key) {
                return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
            }
            return { key, dir: 'asc' };
        });
    };

    const handleRowClick = (id) => navigate(`/profiles/${id}`);

    const handleExportPdf = () => {
        const filters = describeFilters({
            age: ageFilter,
            vacc: vaccFilter,
            growth: growthFilter,
            gn: gnFilter,
            search: searchQuery,
        });
        const stamp = new Date().toISOString().split('T')[0];
        buildPdfReport({
            title: titleInfo.title,
            subtitle: `${APP_NAME} · ${sortedData.length} children shown`,
            filters,
            sections: [
                {
                    heading: 'Profile List',
                    head: [
                        'Reg No',
                        'Child Name',
                        'Category',
                        'DOB',
                        "Mother's No",
                        'Vaccination',
                        'Growth',
                        'GN Division',
                        ...(isAdmin ? ['Added by'] : []),
                    ],
                    body: sortedData.map((c) => [
                        c.childNo || '-',
                        c.name || '-',
                        getCategory(c.dob),
                        formatDateDisplay(c.dob),
                        c.mothersNo || '-',
                        c.vaccinationStatus === 'Completed'
                            ? 'Up to Date'
                            : c.vaccinationStatus || 'Up to Date',
                        Array.isArray(c.growthStatuses) && c.growthStatuses.length > 0
                            ? c.growthStatuses.join(', ')
                            : GROWTH_STATUS_DEFAULT,
                        c.gnDevision || '-',
                        ...(isAdmin ? [c.ownerIdCode || '-'] : []),
                    ]),
                },
            ],
            fileName: `Profiles_${stamp}.pdf`,
            toast,
        });
    };

    if (loading) {
        return (
            <div className="profiles-wrapper">
                <PageHeader title="Registered Profiles" subtitle="Loading…" icon={FiUsers} />
                <div className="profiles-card">
                    <table className="profiles-table">
                        <thead>
                            <tr>
                                {COLUMNS.map((c) => (
                                    <th key={c.key} style={{ width: c.width }}>{c.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: 8 }).map((_, i) => (
                                <SkeletonTableRow key={i} cols={COLUMNS.length} />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="profiles-wrapper">
                <EmptyState
                    illustration="error"
                    title="Couldn't load profiles"
                    description={error.message || String(error)}
                    action={
                        <PageButton
                            type="button"
                            variant="primary"
                            onClick={() => window.location.reload()}
                        >
                            Try again
                        </PageButton>
                    }
                />
            </div>
        );
    }

    return (
        <div className="profiles-wrapper">
            <PageHeader
                title={titleInfo.title}
                subtitle={titleInfo.subtitle}
                icon={FiUsers}
            />

            <div
                className="profiles-stat-bar"
                role="status"
                aria-live="polite"
                aria-label="Current filter result"
            >
                {activeCards.map((c) => (
                    <div
                        key={c.key}
                        className={`profiles-stat profiles-stat--${c.severity}`}
                    >
                        <span className="profiles-stat__label">{c.label}</span>
                        <span className="profiles-stat__value">{c.count}</span>
                    </div>
                ))}
            </div>

            <div className="profiles-card">
                <div className="profiles-actions">
                    <div className="filter-group">
                        <div className="filter-dropdown">
                            <select
                                value={ageFilter}
                                onChange={(e) => setAgeFilter(e.target.value)}
                                aria-label="Filter by age"
                            >
                                <option value="All">All Ages</option>
                                <option value="EarlyChildhood">Early Childhood (0–2 yrs)</option>
                                <option value="Preschool">Preschool Age (2–5 yrs)</option>
                            </select>
                        </div>
                        <div className="filter-dropdown">
                            <select
                                value={vaccFilter}
                                onChange={(e) => setVaccFilter(e.target.value)}
                                aria-label="Filter by vaccination status"
                            >
                                <option value="All">All Vaccination</option>
                                <optgroup label="Needs attention">
                                    <option value="Overdue">Overdue</option>
                                    <option value="Due">Due</option>
                                    <option value="Upcoming">Upcoming</option>
                                </optgroup>
                                <optgroup label="Done">
                                    <option value="Up to Date">Up to Date</option>
                                </optgroup>
                            </select>
                        </div>
                        <div className="filter-dropdown">
                            <select
                                value={growthFilter}
                                onChange={(e) => setGrowthFilter(e.target.value)}
                                aria-label="Filter by growth status"
                            >
                                <option value="All">All Growth</option>
                                {GROWTH_STATUSES.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filter-dropdown">
                            <select
                                value={gnFilter}
                                onChange={(e) => setGnFilter(e.target.value)}
                                aria-label="Filter by GN division"
                            >
                                <option value="All">All GN Divisions</option>
                                {uniqueGNDivisions.map((gn) => (
                                    <option key={gn} value={gn}>{gn}</option>
                                ))}
                            </select>
                        </div>
                        <SearchInput
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onClear={() => setSearchQuery('')}
                            placeholder="Search by name, reg no or mother's no"
                            ariaLabel="Search profiles"
                        />
                    </div>
                    <PageButton
                        type="button"
                        variant="secondary"
                        startIcon={<FiDownload size={16} />}
                        onClick={handleExportPdf}
                        disabled={sortedData.length === 0}
                    >
                        Export PDF
                    </PageButton>
                </div>

                <div className="table-responsive">
                    <table className="profiles-table">
                        <thead>
                            <tr>
                                {COLUMNS.map((col) => {
                                    const active = sort.key === col.key;
                                    return (
                                        <th
                                            key={col.key}
                                            style={{ width: col.width }}
                                            className={col.sortable ? 'sortable' : ''}
                                            aria-sort={
                                                active
                                                    ? sort.dir === 'asc'
                                                        ? 'ascending'
                                                        : 'descending'
                                                    : 'none'
                                            }
                                        >
                                            {col.sortable ? (
                                                <button
                                                    type="button"
                                                    onClick={() => toggleSort(col.key)}
                                                    className="profiles-th-btn"
                                                >
                                                    <span>{col.label}</span>
                                                    {active ? (
                                                        sort.dir === 'asc' ? (
                                                            <FiArrowUp size={12} />
                                                        ) : (
                                                            <FiArrowDown size={12} />
                                                        )
                                                    ) : null}
                                                </button>
                                            ) : (
                                                col.label
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData.map((child) => {
                                // Per-child vaccination breakdown. BE returns
                                // `vaccinationCounts: {completed, action, upcoming,
                                // due, overdue}` from /profiles. Render every
                                // non-zero bucket as its own pill, highest-
                                // priority first, falling through to "Up to
                                // Date" when nothing is pending.
                                const counts = child.vaccinationCounts || {};
                                // Profiles shows the real per-bucket dose count
                                // (e.g. "Overdue 2"); Dashboard and
                                // Reports/VaccineSummaries keep the 0/1 rule
                                // — see that comment for the cross-page
                                // contract.
                                const vaccPills = [
                                    { key: 'overdue',  count: counts.overdue  || 0, label: 'Overdue',  severity: 'danger'  },
                                    { key: 'due',      count: counts.due      || 0, label: 'Due',      severity: 'warning' },
                                    { key: 'upcoming', count: counts.upcoming || 0, label: 'Upcoming', severity: 'info'    },
                                ].filter((p) => p.count > 0);
                                const vaccAllClear = vaccPills.length === 0;
                                // New: backend sends list-of-statuses. Each
                                // status gets its own pill, mirroring the
                                // Vaccination column. Empty list → Unknown.
                                const growthStatuses =
                                    Array.isArray(child.growthStatuses) && child.growthStatuses.length > 0
                                        ? child.growthStatuses
                                        : [GROWTH_STATUS_DEFAULT];
                                const category = getCategory(child.dob);
                                return (
                                    <tr
                                        key={child.id}
                                        className="profiles-table-row"
                                        onClick={() => handleRowClick(child.id)}
                                    >
                                        <td className="child-no-cell">{child.childNo}</td>
                                        <td className="child-name-cell">
                                            <div className="profiles-name-wrap">
                                                <Avatar
                                                    name={child.name}
                                                    id={child.id || child.childNo}
                                                    size={32}
                                                />
                                                <span>{child.name}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <StatusBadge severity={categorySeverity(category)}>
                                                {category}
                                            </StatusBadge>
                                        </td>
                                        <td>{formatDateDisplay(child.dob)}</td>
                                        <td>{child.mothersNo}</td>
                                        <td>
                                            <Tooltip title="Open vaccination tracking" placement="top">
                                                <span
                                                    className="profiles-vacc-stack"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/vaccination/${child.id}`);
                                                    }}
                                                >
                                                    {vaccAllClear ? (
                                                        <StatusBadge
                                                            severity="success"
                                                            className="profiles-badge-link"
                                                        >
                                                            Up to Date
                                                        </StatusBadge>
                                                    ) : (
                                                        vaccPills.map((p) => (
                                                            <StatusBadge
                                                                key={p.key}
                                                                severity={p.severity}
                                                                className="profiles-badge-link"
                                                            >
                                                                {p.label} {p.count}
                                                            </StatusBadge>
                                                        ))
                                                    )}
                                                </span>
                                            </Tooltip>
                                        </td>
                                        <td>
                                            <Tooltip title="Open growth tracking" placement="top">
                                                <span
                                                    className="profiles-vacc-stack"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/growth/${child.id}`);
                                                    }}
                                                >
                                                    {growthStatuses.map((g) => (
                                                        <StatusBadge
                                                            key={g}
                                                            severity={growthSeverity(g)}
                                                            className="profiles-badge-link"
                                                        >
                                                            {g}
                                                        </StatusBadge>
                                                    ))}
                                                </span>
                                            </Tooltip>
                                        </td>
                                        <td>{child.gnDevision || '—'}</td>
                                        {isAdmin && (
                                            <td className="profiles-owner-cell">
                                                {child.ownerIdCode || '—'}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                            {sortedData.length === 0 && (
                                <tr>
                                    <td colSpan={COLUMNS.length} className="profiles-empty-cell">
                                        <EmptyState
                                            illustration={searchQuery ? 'search' : 'filter'}
                                            compact
                                            title={
                                                searchQuery
                                                    ? 'No profiles match your search'
                                                    : 'No profiles match this filter'
                                            }
                                            action={
                                                searchQuery ? (
                                                    <PageButton
                                                        type="button"
                                                        variant="ghost"
                                                        onClick={() => setSearchQuery('')}
                                                    >
                                                        Clear search
                                                    </PageButton>
                                                ) : null
                                            }
                                        />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Profiles;
